/**
 * CompanyMetadata - Displays structured company metadata
 * Professional, organized layout with icons and better visual hierarchy
 * All data displayed in cards/tables, never raw JSON
 */
import {
  Briefcase,
  Globe,
  Target,
  ShoppingBag,
  TrendingUp,
  UserCircle,
  AlertCircle,
  Link2,
  Sparkles,
} from 'lucide-react'
import { Card } from '../ui/Card'

interface CompanyMetadataProps {
  metadata: Record<string, any>
  compact?: boolean
  excludeFields?: string[] // Fields to exclude from display
}

export function CompanyMetadata({ metadata, excludeFields = [] }: CompanyMetadataProps) {
  if (!metadata || Object.keys(metadata).length === 0) {
    return null
  }

  // Merge companyinfo into overview data (skip description/name - already in CompanyInfo)
  const companyInfo = metadata.companyinfo || metadata.companyInfo
  const hasCompanyInfoData = companyInfo && typeof companyInfo === 'object'
  const isEmpty = (v: unknown) =>
    v == null || v === '' || (typeof v === 'string' && v.toLowerCase().includes('not available'))
  const companyInfoFields = hasCompanyInfoData
    ? {
        industry: isEmpty(companyInfo.industry) ? null : companyInfo.industry,
        mission: isEmpty(companyInfo.mission) ? null : companyInfo.mission,
        narrative: isEmpty(companyInfo.narrative) ? null : companyInfo.narrative,
        salesModel: isEmpty(companyInfo.salesModel) ? null : companyInfo.salesModel,
        targetAudience: isEmpty(companyInfo.targetAudience) ? null : companyInfo.targetAudience,
        values: Array.isArray(companyInfo.values) && companyInfo.values.length > 0 ? companyInfo.values : null,
      }
    : {}

  // Root-level metadata (from former Additional Information) - merge into sections, skip duplicates
  const rootEmail = metadata.email
  const rootLocations = Array.isArray(metadata.locations) ? metadata.locations : metadata.locations
  const rootProductsDescription = metadata.productsDescription
  const rootSocialProof = metadata.socialProof && typeof metadata.socialProof === 'object' ? metadata.socialProof : null
  const rootIndustry = metadata.industry
  const rootTargetAudience = metadata.targetAudience

  const hasOverview = metadata.overview && typeof metadata.overview === 'object'
  const hasCompanyInfoFields = Object.values(companyInfoFields).some((v) => v != null && v !== '' && (Array.isArray(v) ? v.length > 0 : true))
  const hasSocialProofData = rootSocialProof && (
    (rootSocialProof.claims && Array.isArray(rootSocialProof.claims) && rootSocialProof.claims.some((c: string) => c && !isEmpty(c))) ||
    (rootSocialProof.pressMentions && !isEmpty(rootSocialProof.pressMentions))
  )
  const hasRootData = (rootEmail && !isEmpty(rootEmail)) ||
    (rootLocations && (Array.isArray(rootLocations) ? rootLocations.length > 0 : rootLocations)) ||
    hasSocialProofData ||
    (rootIndustry && !isEmpty(rootIndustry)) ||
    (rootTargetAudience && !isEmpty(rootTargetAudience))
  const hasOverviewOrCompanyInfo = hasOverview || hasCompanyInfoFields || hasRootData

  return (
    <div className="space-y-6">
      {/* Overview Section - merged with companyinfo (no duplicates) */}
      {hasOverviewOrCompanyInfo && (
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 bg-tf-feedback-info-bg rounded-lg flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-tf-feedback-info-text" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-tf-text">Executive Summary</h3>
              <p className="text-xs text-tf-text-muted">General business information</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {hasOverview && (
              <>
                {metadata.overview.businessName && (
                  <div className="p-4 bg-tf-bg-muted rounded-lg">
                    <label className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide mb-1 block">
                      Business Name
                    </label>
                    <p className="text-sm font-medium text-tf-text">
                      {metadata.overview.businessName}
                    </p>
                  </div>
                )}
                {metadata.overview.website && (
                  <div className="p-4 bg-tf-bg-muted rounded-lg">
                    <label className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide mb-1 block">
                      Website
                    </label>
                    <a
                      href={metadata.overview.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-primary hover:text-primary-dark inline-flex items-center gap-1"
                    >
                      {metadata.overview.website}
                      <Globe className="w-3.5 h-3.5" />
                    </a>
                  </div>
                )}
                {metadata.overview.contact && (
                  <div className="p-4 bg-tf-bg-muted rounded-lg">
                    <label className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide mb-1 block">
                      Contact
                    </label>
                    <p className="text-sm font-medium text-tf-text">{metadata.overview.contact}</p>
                  </div>
                )}
                {metadata.overview.market && (
                  <div className="p-4 bg-tf-bg-muted rounded-lg md:col-span-2">
                    <label className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide mb-1 block">
                      Market
                    </label>
                    <p className="text-sm text-tf-text-sub leading-relaxed whitespace-pre-wrap">
                      {metadata.overview.market}
                    </p>
                  </div>
                )}
                {metadata.overview.positioning && (
                  <div className="p-4 bg-tf-bg-muted rounded-lg md:col-span-2">
                    <label className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide mb-1 block">
                      Positioning
                    </label>
                    <p className="text-sm text-tf-text-sub leading-relaxed whitespace-pre-wrap">
                      {metadata.overview.positioning}
                    </p>
                  </div>
                )}
              </>
            )}
            {/* companyinfo fields - only non-empty, non-duplicate */}
            {companyInfoFields.industry && (
              <div className="p-4 bg-tf-bg-muted rounded-lg">
                <label className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide mb-1 block">
                  Industry
                </label>
                <p className="text-sm font-medium text-tf-text">{companyInfoFields.industry}</p>
              </div>
            )}
            {companyInfoFields.mission && (
              <div className="p-4 bg-tf-bg-muted rounded-lg md:col-span-2">
                <label className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide mb-1 block">
                  Mission
                </label>
                <p className="text-sm text-tf-text-sub leading-relaxed">{companyInfoFields.mission}</p>
              </div>
            )}
            {companyInfoFields.narrative && (
              <div className="p-4 bg-tf-bg-muted rounded-lg md:col-span-2">
                <label className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide mb-1 block">
                  Narrative
                </label>
                <p className="text-sm text-tf-text-sub leading-relaxed">{companyInfoFields.narrative}</p>
              </div>
            )}
            {companyInfoFields.salesModel && (
              <div className="p-4 bg-tf-bg-muted rounded-lg">
                <label className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide mb-1 block">
                  Sales Model
                </label>
                <p className="text-sm font-medium text-tf-text">{companyInfoFields.salesModel}</p>
              </div>
            )}
            {companyInfoFields.targetAudience && (
              <div className="p-4 bg-tf-bg-muted rounded-lg md:col-span-2">
                <label className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide mb-1 block">
                  Target Audience
                </label>
                <p className="text-sm text-tf-text-sub leading-relaxed">{companyInfoFields.targetAudience}</p>
              </div>
            )}
            {companyInfoFields.values && Array.isArray(companyInfoFields.values) && companyInfoFields.values.length > 0 && (
              <div className="p-4 bg-tf-bg-muted rounded-lg md:col-span-2">
                <label className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide mb-2 block">
                  Values
                </label>
                <div className="flex flex-wrap gap-2">
                  {companyInfoFields.values.map((item: string, idx: number) => (
                    <span
                      key={idx}
                      className="inline-flex items-center px-2.5 py-1 rounded-md text-sm bg-tf-bg-hover text-tf-text-sub border border-tf-border"
                    >
                      {typeof item === 'object' ? JSON.stringify(item) : String(item)}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {/* Root-level metadata (from former Additional Information) - no duplicates */}
            {rootEmail && !isEmpty(rootEmail) && !metadata.overview?.contact && (
              <div className="p-4 bg-tf-bg-muted rounded-lg">
                <label className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide mb-1 block">
                  Email
                </label>
                <a href={`mailto:${rootEmail}`} className="text-sm font-medium text-primary hover:text-primary-dark">
                  {rootEmail}
                </a>
              </div>
            )}
            {rootIndustry && !isEmpty(rootIndustry) && !companyInfoFields.industry && (
              <div className="p-4 bg-tf-bg-muted rounded-lg">
                <label className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide mb-1 block">
                  Industry
                </label>
                <p className="text-sm font-medium text-tf-text">{rootIndustry}</p>
              </div>
            )}
            {rootTargetAudience && !isEmpty(rootTargetAudience) && !companyInfoFields.targetAudience && (
              <div className="p-4 bg-tf-bg-muted rounded-lg md:col-span-2">
                <label className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide mb-1 block">
                  Target Audience
                </label>
                <p className="text-sm text-tf-text-sub leading-relaxed">{rootTargetAudience}</p>
              </div>
            )}
            {rootLocations && (Array.isArray(rootLocations) ? rootLocations.length > 0 : rootLocations) && (
              <div className="p-4 bg-tf-bg-muted rounded-lg md:col-span-2">
                <label className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide mb-2 block">
                  Locations
                </label>
                <div className="flex flex-wrap gap-2">
                  {(Array.isArray(rootLocations) ? rootLocations : [rootLocations]).map((loc: string, idx: number) => (
                    <span
                      key={idx}
                      className="inline-flex items-center px-2.5 py-1 rounded-md text-sm bg-tf-bg-hover text-tf-text-sub border border-tf-border"
                    >
                      {typeof loc === 'object' ? JSON.stringify(loc) : String(loc)}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {rootSocialProof && (
              <div className="p-4 bg-tf-bg-muted rounded-lg md:col-span-2 space-y-3">
                {rootSocialProof.claims && Array.isArray(rootSocialProof.claims) && rootSocialProof.claims.length > 0 && (
                  <div>
                    <label className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide mb-2 block">
                      Claims
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {rootSocialProof.claims
                        .filter((c: string) => c && !isEmpty(c))
                        .map((claim: string, idx: number) => (
                          <span
                            key={idx}
                            className="inline-flex items-center px-2.5 py-1 rounded-md text-sm bg-tf-bg-hover text-tf-text-sub border border-tf-border"
                          >
                            {claim}
                          </span>
                        ))}
                    </div>
                  </div>
                )}
                {rootSocialProof.pressMentions && !isEmpty(rootSocialProof.pressMentions) && (
                  <div>
                    <label className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide mb-1 block">
                      Press Mentions
                    </label>
                    <p className="text-sm text-tf-text-sub leading-relaxed">
                      {Array.isArray(rootSocialProof.pressMentions)
                        ? rootSocialProof.pressMentions.join(' • ')
                        : rootSocialProof.pressMentions}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Social Media Section - merge social_media + socialAccounts (no duplicates) */}
      {(() => {
        const socialMedia = metadata.social_media && typeof metadata.social_media === 'object' ? metadata.social_media : {}
        const socialAccounts = metadata.socialAccounts && typeof metadata.socialAccounts === 'object' ? metadata.socialAccounts : {}
        const merged = { ...socialMedia }
        Object.entries(socialAccounts).forEach(([platform, url]) => {
          if (url && !isEmpty(url) && !merged[platform]) merged[platform] = url
        })
        const hasSocial = Object.keys(merged).length > 0
        if (!hasSocial || excludeFields.includes('social_media')) return null
        return (
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 bg-tf-feedback-info-bg rounded-lg flex items-center justify-center">
                <Link2 className="w-5 h-5 text-tf-feedback-info-text" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-tf-text">Social Media</h3>
                <p className="text-xs text-tf-text-muted">Social profiles and links</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              {Object.entries(merged).map(([platform, url]) =>
                url && !isEmpty(url) ? (
                  <a
                    key={platform}
                    href={typeof url === 'string' ? url : String(url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-tf-bg-card border border-tf-border text-sm font-medium text-tf-text-sub hover:bg-tf-bg-hover hover:border-tf-border-hover transition-all shadow-sm"
                  >
                    <span className="capitalize">{platform}</span>
                    <Globe className="w-3.5 h-3.5 text-tf-text-sub" />
                  </a>
                ) : null
              )}
            </div>
          </Card>
        )
      })()}

      {/* Positioning Section */}
      {metadata.positioning && (
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 bg-tf-feedback-success-bg rounded-lg flex items-center justify-center">
              <Target className="w-5 h-5 text-tf-feedback-success-text" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-tf-text">Positioning</h3>
              <p className="text-xs text-tf-text-muted">Value proposition and differentiators</p>
            </div>
          </div>
          <div className="space-y-4">
            {typeof metadata.positioning === 'object' && metadata.positioning !== null && (
              <>
                {metadata.positioning.valueProposition && (
                  <div className="p-4 bg-tf-feedback-success-bg rounded-lg border border-tf-border">
                    <label className="text-xs font-semibold text-tf-feedback-success-text uppercase tracking-wide mb-2 block">
                      Value Proposition
                    </label>
                    <p className="text-sm text-tf-text leading-relaxed">
                      {metadata.positioning.valueProposition}
                    </p>
                  </div>
                )}
                {metadata.positioning.differentiators &&
                  Array.isArray(metadata.positioning.differentiators) && (
                    <div className="p-4 bg-tf-bg-muted rounded-lg">
                      <label className="text-xs font-semibold text-tf-text-sub uppercase tracking-wide mb-2 block">
                        Differentiators
                      </label>
                      <ul className="space-y-2">
                        {metadata.positioning.differentiators.map((item: string, idx: number) => (
                          <li key={idx} className="flex items-start gap-2">
                            <Sparkles className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-tf-text-sub">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
              </>
            )}
          </div>
        </Card>
      )}

      {/* Products & Features Section - includes productsDescription from root metadata */}
      {(metadata.products || (rootProductsDescription && !isEmpty(rootProductsDescription))) && (
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 bg-tf-bg-hover rounded-lg flex items-center justify-center">
              <ShoppingBag className="w-5 h-5 text-tf-text-sub" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-tf-text">Products & Features</h3>
              <p className="text-xs text-tf-text-muted">Catalog and key benefits</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {typeof metadata.products === 'object' && metadata.products !== null && (
              <>
                {metadata.products.categories && (
                  <div className="p-4 bg-tf-bg-muted rounded-lg">
                    <label className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide mb-1 block">
                      Categories
                    </label>
                    <p className="text-sm font-medium text-tf-text">
                      {metadata.products.categories}
                    </p>
                  </div>
                )}
                {metadata.products.priceRange && (
                  <div className="p-4 bg-tf-bg-muted rounded-lg">
                    <label className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide mb-1 block">
                      Price Range
                    </label>
                    <p className="text-sm font-medium text-tf-text">
                      {metadata.products.priceRange}
                    </p>
                  </div>
                )}
                {metadata.products.coreBenefits && (
                  <div className="p-4 bg-tf-bg-muted rounded-lg md:col-span-2">
                    <label className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide mb-1 block">
                      Core Benefits
                    </label>
                    <p className="text-sm text-tf-text-sub leading-relaxed">
                      {metadata.products.coreBenefits}
                    </p>
                  </div>
                )}
              </>
            )}
            {rootProductsDescription && !isEmpty(rootProductsDescription) && !metadata.products?.coreBenefits && (
              <div className="p-4 bg-tf-bg-muted rounded-lg md:col-span-2">
                <label className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide mb-1 block">
                  Products Description
                </label>
                <p className="text-sm text-tf-text-sub leading-relaxed">{rootProductsDescription}</p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Competitors Section */}
      {metadata.competitors &&
        Array.isArray(metadata.competitors) &&
        metadata.competitors.length > 0 && (
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 bg-tf-feedback-error-bg rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-tf-feedback-error-text" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-tf-text">Competitors</h3>
                <p className="text-xs text-tf-text-muted">
                  Competitive analysis ({metadata.competitors.length} found)
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {metadata.competitors.map((competitor: any, idx: number) => (
                <div
                  key={idx}
                  className="p-4 bg-tf-bg-muted rounded-lg border border-tf-border hover:border-tf-border transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-tf-text">
                      {competitor.name || `Competitor ${idx + 1}`}
                    </h4>
                    {competitor.match && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-tf-feedback-info-bg text-tf-feedback-info-text border border-tf-border">
                        {competitor.match}% match
                      </span>
                    )}
                  </div>
                  {competitor.description && (
                    <p className="text-sm text-tf-text-muted leading-relaxed">{competitor.description}</p>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}

      {/* Buyer Personas Section */}
      {metadata.buyerPersonas &&
        Array.isArray(metadata.buyerPersonas) &&
        metadata.buyerPersonas.length > 0 && (
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 bg-tf-feedback-info-bg rounded-lg flex items-center justify-center">
                <UserCircle className="w-5 h-5 text-tf-feedback-info-text" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-tf-text">Buyer Personas</h3>
                <p className="text-xs text-tf-text-muted">
                  Target customer profiles ({metadata.buyerPersonas.length} personas)
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {metadata.buyerPersonas.map((persona: any, idx: number) => (
                <div
                  key={idx}
                  className="p-5 bg-tf-bg-muted rounded-lg border border-tf-border"
                >
                  <h4 className="font-semibold text-tf-text mb-2 text-lg">
                    {persona.name || `Persona ${idx + 1}`}
                  </h4>
                  {persona.description && (
                    <p className="text-sm text-tf-text-sub mb-4 leading-relaxed">
                      {persona.description}
                    </p>
                  )}
                  <div className="space-y-3">
                    {persona.painPoints && (
                      <div>
                        <label className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide mb-1 block">
                          Pain Points
                        </label>
                        <ul className="space-y-1">
                          {Array.isArray(persona.painPoints)
                            ? persona.painPoints.map((point: string, i: number) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-tf-text-sub">
                                  <AlertCircle className="w-3.5 h-3.5 text-tf-feedback-error-text mt-0.5 flex-shrink-0" />
                                  <span>{point}</span>
                                </li>
                              ))
                            : (
                              <li className="flex items-start gap-2 text-sm text-tf-text-sub">
                                <AlertCircle className="w-3.5 h-3.5 text-tf-feedback-error-text mt-0.5 flex-shrink-0" />
                                <span>{persona.painPoints}</span>
                              </li>
                            )}
                        </ul>
                      </div>
                    )}
                    {persona.desiredOutcomes && (
                      <div>
                        <label className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide mb-1 block">
                          Desired Outcomes
                        </label>
                        <ul className="space-y-1">
                          {Array.isArray(persona.desiredOutcomes)
                            ? persona.desiredOutcomes.map((outcome: string, i: number) => (
                                <li
                                  key={i}
                                  className="flex items-start gap-2 text-sm text-tf-text-sub"
                                >
                                  <Sparkles className="w-3.5 h-3.5 text-tf-feedback-success-text mt-0.5 flex-shrink-0" />
                                  <span>{outcome}</span>
                                </li>
                              ))
                            : (
                              <li className="flex items-start gap-2 text-sm text-tf-text-sub">
                                <Sparkles className="w-3.5 h-3.5 text-tf-feedback-success-text mt-0.5 flex-shrink-0" />
                                <span>{persona.desiredOutcomes}</span>
                              </li>
                            )}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

    </div>
  )
}
